import {useState, useMemo, useEffect} from "react";
import {useLocation} from "react-router-dom";
import {App as AntdApp, Segmented, Select, Spin} from "antd";
import className from "classnames/bind";
import {FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetAiTasksQuery, useGetAiStatusQuery, useGenerateAiMutation} from "~/reduxs/api/aiApiSlice";
import AiTaskForm from "./AiTaskForm";
import AiResult from "./AiResult";
import AiKeyAlert from "./AiKeyAlert";
import TokenRemaining from "./TokenRemaining";
import style from "../style/AiTool.module.scss";

const cn = className.bind(style);

/**
 * Bộ "sinh nội dung AI" (tác vụ #4: meta/FAQ/dàn ý). Dùng chung cho màn AI độc lập
 * (`/ai`, projectId=0) và tab "Sinh nội dung" trong dự án. Tự tải danh sách tác vụ +
 * trạng thái cấu hình khoá; chọn tác vụ → form động → generate → hiển thị kết quả.
 */
function AiGenerator({projectId = 0}) {

	const {notification} = AntdApp.useApp();

	const {data: tasks, isLoading: loadingTasks} = useGetAiTasksQuery();
	// Tự refetch khi mở lại tab / quay lại cửa sổ → tránh bám kết quả "chưa cấu hình"
	// cũ bị cache (backend đọc .env tươi mỗi request; status cũ có thể lỡ nhịp).
	const {data: status} = useGetAiStatusQuery(undefined, {
		refetchOnMountOrArgChange: true,
		refetchOnFocus: true,
		refetchOnReconnect: true,
	});
	const [generateAi, {isLoading}] = useGenerateAiMutation();

	// Prefill khi điều hướng kèm state {task, input} — cùng pattern SchemaValidator
	// (vd nút "Gợi ý bài viết" ở /trends → task trend_content với từ khoá + tin tức).
	const location = useLocation();
	const prefill = location.state?.task ? location.state : null;

	const [activeTask, setActiveTask] = useState(null);
	const [result, setResult] = useState(null);

	useEffect(() => {
		if (tasks?.length && !activeTask) {
			const wanted = prefill?.task && tasks.some((t) => t.key === prefill.task)
				? prefill.task
				: tasks[0].key;
			setActiveTask(wanted);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tasks, activeTask]);

	const task = useMemo(() => (tasks || []).find((t) => t.key === activeTask), [tasks, activeTask]);
	const notConfigured = status && !status.configured;

	const handleGenerate = async (input) => {
		try {
			const data = await generateAi({
				task: activeTask,
				project_id: projectId ? Number(projectId) : 0,
				input,
			}).unwrap();
			setResult(data);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Tạo nội dung thất bại')});
		}
	};

	if (loadingTasks) return <Loading />;

	return (
		<div>
			{notConfigured && (
				<AiKeyAlert
					configured={false}
					requireOwnKey={Boolean(status.require_own_key)}
					provider={status.provider}
					style={{marginBottom: 16}}
				/>
			)}

			<div className={cn('taskBar')}>
				{/* >8 tác vụ thì Segmented tràn ngang → chuyển sang Select. */}
				{(tasks || []).length > 8 ? (
					<Select
						value={activeTask}
						onChange={setActiveTask}
						style={{minWidth: 260}}
						options={(tasks || []).map((t) => ({label: t.label, value: t.key}))}
					/>
				) : (
					<Segmented
						value={activeTask}
						onChange={setActiveTask}
						options={(tasks || []).map((t) => ({label: t.label, value: t.key}))}
					/>
				)}
				<TokenRemaining />
			</div>

			{task && (
				<div className={cn('grid')}>
					<div className={cn('formCol')}>
						<p className={cn('desc')}>{task.description}</p>
						<AiTaskForm
							key={task.key}
							task={task}
							loading={isLoading}
							disabled={notConfigured}
							prefill={prefill?.task === task.key ? prefill.input : null}
							onSubmit={handleGenerate}
						/>
					</div>
					<div className={cn('resultCol')}>
						{isLoading && (
							<div className={cn('loading')}>
								<Spin />
								<p>AI đang xử lý, vui lòng đợi…</p>
							</div>
						)}
						{!isLoading && result && <AiResult result={result} />}
						{!isLoading && !result && (
							<div className={cn('placeholder')}>
								<FontAwesomeIcon icon="fa-light fa-wand-magic-sparkles" />
								<p>Nhập thông tin bên trái và bấm <strong>Tạo nội dung</strong> để nhận kết quả.</p>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

export default AiGenerator;
